import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
  CarRentalType,
  CarSearchRequest,
  SearchType,
} from '../../../../shared/models/search-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { SearchBarService } from '../../services/search.service';
import { map, Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DataService } from '../../../../shared/data/data.service';
import { GeolocationService } from '../../../../shared/services/geolocation.service';
import {
  BsDaterangepickerDirective,
  BsDaterangepickerConfig,
} from 'ngx-bootstrap/datepicker';

@Component({
  selector: 'app-car-search',
  standalone: false,
  templateUrl: './car-search.component.html',
  styleUrl: './car-search.component.scss',
})
export class CarSearchComponent implements OnInit, OnDestroy {
  @ViewChild('dateRangePicker', { static: false })
  dateRangePicker!: BsDaterangepickerDirective;

  isLoading: boolean = false;

  // Range picker configuration with mobile optimization
  bsRangeConfig: Partial<BsDaterangepickerConfig> = {
    dateInputFormat: 'DD-MM-YYYY',
    containerClass: 'theme-default mobile-responsive',
    showWeekNumbers: false,
    adaptivePosition: false,
    isAnimated: true,
    rangeInputFormat: 'DD-MM-YYYY',
  };

  @Output() search: EventEmitter<string> = new EventEmitter<string>();

  countries$!: Observable<any[]>;
  carSearchForm!: FormGroup;
  bsValue = new Date(); // Used as minDate for date pickers
  time: Date | null = null;
  now = new Date();
  pad = (n: number) => (n < 10 ? '0' + n : n);
  dateStr = `${this.now.getFullYear()}-${this.pad(this.now.getMonth() + 1)}-${this.pad(this.now.getDate())}`;
  hours = this.now.getHours();
  minutes = this.now.getMinutes();
  // 24-hour format string for UI and API
  timeStr = `${this.pad(this.hours)}:${this.pad(this.minutes)}`;
  private readonly allowedCarSearchURLs = ['home', 'car-list', 'car-details'];

  get carRentalTypeEnum(): typeof CarRentalType {
    return CarRentalType;
  }

  get searchTypeEnum(): typeof SearchType {
    return SearchType;
  }

  private _autoSaveSub?: Subscription;
  private _suppressAutoSave: boolean = false;

  constructor(
    private readonly searchBarService: SearchBarService,
    private readonly dataService: DataService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly geo: GeolocationService,
    private readonly _cdr: ChangeDetectorRef,
  ) {
    this.countries$ = this.dataService
      .getCountryCodes()
      .pipe(
        map((options) =>
          options.map((option) => ({ label: option.name, code: option.code })),
        ),
      );
    this.searchBarService.isLoading$.subscribe((loading: boolean) => {
      this.isLoading = loading;
    });
  }

  ngOnInit() {
    this.initializeForm();
    this.getLocalStorageValues();
    this.setupFormSubscriptions();
    this.prefillNearestPickupIfEmpty();
    // Clamp driver age within [18, 100]
    const ageCtrl = this.carSearchForm.get('driver_age');
    ageCtrl?.valueChanges.subscribe((val) => {
      const n = Number(val);
      if (isNaN(n)) return;
      const clamped = Math.min(100, Math.max(18, n));
      if (clamped !== n) {
        ageCtrl.setValue(clamped, { emitEvent: false });
      }
    });
  }






  // Handle date range change from ngx-bootstrap date range picker
onDateRangeChange(dateRange: (Date | undefined)[] | undefined) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!dateRange || !dateRange[0]) {
    this.carSearchForm.patchValue({
      pickup_date: this.getTodayStr(),
      dropoff_date: this.getNextDateStr(1)
    });
    return;
  }

  let pickup = new Date(dateRange[0]);
  pickup.setHours(0, 0, 0, 0);

  let dropoff = dateRange[1] ? new Date(dateRange[1]) : null;

  // Prevent past pickup
  if (pickup < today) {
    pickup = today;
  }

  // Ensure dropoff > pickup
  if (!dropoff || dropoff <= pickup) {
    dropoff = new Date(pickup);
    dropoff.setDate(pickup.getDate() + 1);
  }

  this.carSearchForm.patchValue({
    pickup_date: this.formatDate(pickup),
    dropoff_date: this.formatDate(dropoff)
  });
}

  // Format date to YYYY-MM-DD string
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  private getTodayStr(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return this.formatDate(d);
}

private getNextDateStr(days: number = 1): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return this.formatDate(d);
}

private normalizeCarDates(): void {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let pickup = new Date(this.carSearchForm.get('pickup_date')?.value);
  let dropoff = new Date(this.carSearchForm.get('dropoff_date')?.value);

  if (isNaN(pickup.getTime()) || pickup < today) {
    pickup = today;
  }

  if (isNaN(dropoff.getTime()) || dropoff <= pickup) {
    dropoff = new Date(pickup);
    dropoff.setDate(pickup.getDate() + 1);
  }

  this.carSearchForm.patchValue({
    pickup_date: this.formatDate(pickup),
    dropoff_date: this.formatDate(dropoff)
  }, { emitEvent: false });
}


  // Get formatted date for display in the input fields
  getFormattedDate(fieldName: 'pickup_date' | 'dropoff_date'): string {
    const dateValue = this.carSearchForm.get(fieldName)?.value;
    if (dateValue) {
      // If it's a string in YYYY-MM-DD, convert to DD-MM-YYYY for UI display
      if (
        typeof dateValue === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
      ) {
        const [y, m, d] = dateValue.split('-');
        return `${d}-${m}-${y}`;
      }
      // If it's a Date object, format as DD-MM-YYYY
      if (dateValue instanceof Date) {
        const year = dateValue.getFullYear();
        const month = ('0' + (dateValue.getMonth() + 1)).slice(-2);
        const day = ('0' + dateValue.getDate()).slice(-2);
        return `${day}-${month}-${year}`;
      }
    }
    return '';
  }

  // Get date label (day name) for display
  getDateLabel(fieldName: 'pickup_date' | 'dropoff_date'): string {
    const dateValue = this.carSearchForm.get(fieldName)?.value;
    if (dateValue) {
      let date: Date;
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        return '';
      }

      if (!isNaN(date.getTime())) {
        const days = [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ];
        return days[date.getDay()];
      }
    }
    return fieldName === 'pickup_date'
      ? 'Select departure date'
      : 'Select return date';
  }

  // Open the hidden date range picker
  openDateRangePicker(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Add body class for mobile scroll prevention
    if (window.innerWidth <= 767) {
      document.body.classList.add('datepicker-open');
    }

    if (this.dateRangePicker) {
      // Use the ngx-bootstrap directive's show method
      this.dateRangePicker.show();
    }

    // Add mobile overlay close functionality
    setTimeout(() => {
      this.addMobileCloseHandler();
    }, 50); // Reduced timeout for faster execution

    // Force mobile styles more aggressively
    if (window.innerWidth <= 767) {
      setTimeout(() => {
        this.forceMobileStylesAggressively();
      }, 10);
    }
  }

  // Add click handler for mobile overlay close
  private addMobileCloseHandler(): void {
    const datePickerContainer = document.querySelector(
      '.bs-datepicker-container',
    );
    if (datePickerContainer && window.innerWidth <= 767) {
      const overlay = datePickerContainer as HTMLElement;

      // Force override inline styles applied by ngx-bootstrap on mobile
      this.forceMobileStyles(overlay);

      const handleOverlayClick = (event: Event) => {
        const target = event.target as HTMLElement;

        // Close if clicking on the overlay background (not on the datepicker itself)
        if (
          target === overlay ||
          target.classList.contains('bs-datepicker-container')
        ) {
          this.closeDatePicker();
          overlay.removeEventListener('click', handleOverlayClick);
          document.removeEventListener('keydown', handleEscapeKey);
        }

        // Close if clicking on the pseudo-element close button
        if (target === overlay) {
          const rect = overlay.getBoundingClientRect();
          const clickX = (event as MouseEvent).clientX - rect.left;
          const clickY = (event as MouseEvent).clientY - rect.top;

          // Check if click is in the close button area (top-right corner)
          if (clickX > rect.width - 70 && clickY < 70) {
            this.closeDatePicker();
            overlay.removeEventListener('click', handleOverlayClick);
            document.removeEventListener('keydown', handleEscapeKey);
          }
        }
      };

      // Also listen for escape key
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.closeDatePicker();
          overlay.removeEventListener('click', handleOverlayClick);
          document.removeEventListener('keydown', handleEscapeKey);
        }
      };

      overlay.addEventListener('click', handleOverlayClick);
      document.addEventListener('keydown', handleEscapeKey);
    }
  }

  // Force mobile styles to override ngx-bootstrap inline styles
  private forceMobileStyles(container: HTMLElement): void {
    if (window.innerWidth <= 767) {
      // Force override the inline styles
      container.style.setProperty('position', 'fixed', 'important');
      container.style.setProperty('top', '0px', 'important');
      container.style.setProperty('left', '0px', 'important');
      container.style.setProperty('right', '0px', 'important');
      container.style.setProperty('bottom', '0px', 'important');
      container.style.setProperty('width', 'max-content', 'important');
      container.style.setProperty('height', 'max-content', 'important');
      container.style.setProperty('transform', 'none', 'important');
      container.style.setProperty('will-change', 'auto', 'important');
      container.style.setProperty('display', 'flex', 'important');
      container.style.setProperty('align-items', 'center', 'important');
      container.style.setProperty('justify-content', 'center', 'important');
      container.style.setProperty('background', 'white', 'important');
      container.style.setProperty('z-index', '9999', 'important');
      container.style.setProperty('box-sizing', 'border-box', 'important');
      container.style.setProperty('margin', '0', 'important');

      // Also force styles on the inner datepicker
      const datePicker = container.querySelector(
        '.bs-datepicker',
      ) as HTMLElement;
      if (datePicker) {
        datePicker.style.setProperty('position', 'relative', 'important');
        datePicker.style.setProperty('transform', 'none', 'important');
        datePicker.style.setProperty('top', 'auto', 'important');
        datePicker.style.setProperty('left', 'auto', 'important');
        datePicker.style.setProperty('width', '100%', 'important');
        datePicker.style.setProperty('max-width', '350px', 'important');
        datePicker.style.setProperty('max-height', '85vh', 'important');
      }

      // Monitor for any changes and reapply styles
      const observer = new MutationObserver(() => {
        if (window.innerWidth <= 767 && container.isConnected) {
          this.forceMobileStyles(container);
        }
      });

      observer.observe(container, {
        attributes: true,
        attributeFilter: ['style'],
      });

      // Clean up observer when datepicker is closed
      setTimeout(() => {
        observer.disconnect();
      }, 10000); // Cleanup after 10 seconds max
    }
  }

  // More aggressive approach to force mobile styles
  private forceMobileStylesAggressively(): void {
    if (window.innerWidth <= 767) {
      const containers = document.querySelectorAll('.bs-datepicker-container');
      containers.forEach((container) => {
        const element = container as HTMLElement;

        // Remove any existing transform or positioning
        element.style.removeProperty('transform');
        element.style.removeProperty('will-change');

        // Apply mobile styles with highest priority
        const mobileStyles = `
          position: fixed !important;
          top: 0px !important;
          left: 0px !important;
          right: 0px !important;
          bottom: 0px !important;
          width: max-content !important;
          height: max-content !important;
          transform: none !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          z-index: 9999 !important;
          box-sizing: border-box !important;
          margin: 0 !important;
        `;

        element.setAttribute('style', mobileStyles);

        // Also apply to inner datepicker
        const datePicker = element.querySelector(
          '.bs-datepicker',
        ) as HTMLElement;
        if (datePicker) {
          const innerStyles = `
            position: relative !important;
            transform: none !important;
            top: auto !important;
            left: auto !important;
            width: 100% !important;
            max-width: 350px !important;
            max-height: 85vh !important;
            background: #fff !important;
            border-radius: 8px !important;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5) !important;
            overflow: hidden !important;
            border: none !important;
            margin: 0 !important;
          `;
          datePicker.setAttribute(
            'style',
            datePicker.getAttribute('style') + '; ' + innerStyles,
          );
        }
      });

      // Keep checking and reapplying styles
      let attempts = 0;
      const intervalId = setInterval(() => {
        attempts++;
        if (attempts > 20) {
          // Stop after 2 seconds (20 * 100ms)
          clearInterval(intervalId);
          return;
        }

        const containers = document.querySelectorAll(
          '.bs-datepicker-container',
        );
        containers.forEach((container) => {
          const element = container as HTMLElement;
          if (element.style.transform && element.style.transform !== 'none') {
            this.forceMobileStylesAggressively();
          }
        });
      }, 100);
    }
  }

  // Close date picker method
  closeDatePicker(): void {
    // Remove body class for mobile scroll prevention
    document.body.classList.remove('datepicker-open');

    if (this.dateRangePicker) {
      this.dateRangePicker.hide();
    }
  }

  private setupFormSubscriptions() {
    // Watch for changes in carRentalType
    this.carSearchForm.get('carRentalType')?.valueChanges.subscribe((value) => {
      if (value === CarRentalType.SAME_DROP) {
        // Copy pickup location to dropoff location
        const pickupId = this.carSearchForm.get('pickup_id')?.value;
        this.carSearchForm.patchValue({
          dropoff_id: pickupId,
        });
      }
    });

    // Watch for changes in pickup_id when SAME_DROP is selected
    this.carSearchForm.get('pickup_id')?.valueChanges.subscribe((value) => {
      if (
        this.carSearchForm.get('carRentalType')?.value ===
        CarRentalType.SAME_DROP
      ) {
        this.carSearchForm.patchValue({ dropoff_id: value });
      }
    });
  }
  private getLocalStorageValues() {
    const selectedCountryInfo = localStorage.getItem('selectedCountryInfo');
    if (selectedCountryInfo) {
      try {
        const info = JSON.parse(selectedCountryInfo);
        if (info.currency) {
          this.carSearchForm.get('currency')?.setValue(info.currency);
        }
        if (info.carLanguage) {
          this.carSearchForm.get('language')?.setValue(info.carLanguage);
        }
      } catch (e) {
        console.warn('Invalid JSON in car-search selectedCountryInfo:', e);
      }
    }
  }
  initializeForm() {
    this.carSearchForm = new FormGroup({
      carRentalType: new FormControl(CarRentalType.SAME_DROP, [
        Validators.required,
      ]),
      pickup_id: new FormControl('', [Validators.required]),
      dropoff_id: new FormControl('', [Validators.required]),
      pickup_date: new FormControl(this.getTodayStr(), [Validators.required]),
      dropoff_date: new FormControl(this.getNextDateStr(1), [
        Validators.required,
      ]),
      pickup_time: new FormControl(this.timeStr ?? '', [Validators.required]), // 24-hour format for UI/API
      dropoff_time: new FormControl(this.timeStr ?? '', [Validators.required]), // 24-hour format for UI/API
      driver_age: new FormControl(41, [
        Validators.required,
        Validators.min(18),
        Validators.max(100),
      ]),
      country_res: new FormControl('IN', [Validators.required]),
      currency: new FormControl('USD', [Validators.required]),
      language: new FormControl('en', [Validators.required]),
    });

    // Suppress autosave while we initialize/patch the form
    this._suppressAutoSave = true;

    this.route.queryParams.subscribe((params) => {
      let formValue: Record<string, any> = {};
      if (params && Object.keys(params).length > 0) {
        formValue = this.searchBarService.fromQueryParams(params);
      } else {
        // prefer service cache, else fall back to localStorage
        let localValue: any = this.searchBarService.carSearchRequest as any;
        if (!localValue) {
          const raw = localStorage.getItem('carSearchRequest');
          if (raw) {
            try {
              localValue = JSON.parse(raw) as CarSearchRequest;
              this.searchBarService.changeCarSearchRequest(
                localValue as CarSearchRequest,
              );
            } catch (e) {
              console.warn('Invalid carSearchRequest in localStorage', e);
              localValue = null;
            }
          }
        }

        if (!localValue) {
          // nothing to patch from; leave defaults
          this._suppressAutoSave = false;
          return;
        }

        if (this.router.url.includes('car-list')) {
          formValue = this.searchBarService.fromQueryParams(localValue);
          const querystring = this.searchBarService.toQueryString(localValue);
          this.router.navigateByUrl('/car/car-list?' + querystring);
          return;
        }

        formValue = this.searchBarService.fromQueryParams(localValue);
      }

      // Patch only string or undefined, never null
      if (formValue) {
        Object.keys(formValue).forEach((key) => {
          if (formValue[key] === null) formValue[key] = '';
        });
        const to24Hour = (time: string) => {
          if (!time || typeof time !== 'string') return time;
          const timeRegex = /^(\d{1,2}):(\d{2})\s*([AP]M)?$/i;
          const match = timeRegex.exec(time);
          if (match) {
            let hour = parseInt(match[1], 10);
            const minute = match[2];
            const ampm = match[3] ? match[3].toUpperCase() : '';
            if (ampm === 'PM' && hour < 12) hour += 12;
            if (ampm === 'AM' && hour === 12) hour = 0;
            return `${('0' + hour).slice(-2)}:${minute}`;
          }
          const time24Regex = /^(\d{2}):(\d{2})$/;
          const match24 = time24Regex.exec(time);
          if (match24) return `${match24[1]}:${match24[2]}`;
          return '';
        };
        if (formValue['pickup_time'])
          formValue['pickup_time'] = to24Hour(formValue['pickup_time']);
        if (formValue['dropoff_time'])
          formValue['dropoff_time'] = to24Hour(formValue['dropoff_time']);
        this.carSearchForm.patchValue(formValue);
        this.normalizeCarDates();
        this._cdr.detectChanges();
        this.searchBarService.changeCarSearchRequest(
          formValue as CarSearchRequest,
        );
        if (this.isAllowedCarSearchUrl)
          this.searchBarService.getCarSearchData();
      }

      // After patching allow autosave and set up autosave subscription if missing
      this._suppressAutoSave = false;
      if (!this._autoSaveSub) {
        this._autoSaveSub = this.carSearchForm.valueChanges
          .pipe(debounceTime(400))
          .subscribe((val) => {
            if (this._suppressAutoSave) return;
            try {
              const snapshot = JSON.parse(JSON.stringify(val));
              if (snapshot) {
                localStorage.setItem(
                  'carSearchRequest',
                  JSON.stringify(snapshot),
                );
                this.searchBarService.changeCarSearchRequest(
                  snapshot as CarSearchRequest,
                );
              }
            } catch (e) {
              console.warn('Failed to autosave car form', e);
            }
          });
      }

      // After all patching from params/local, prefill if still empty
      this.prefillNearestPickupIfEmpty();
    });
  }

  private get isAllowedCarSearchUrl(): boolean {
    const url = this.router.url.replace(/^\//, ''); // Remove leading slash if present
    return this.allowedCarSearchURLs.some((allowed) => url.includes(allowed));
  }

  private prefillNearestPickupIfEmpty(): void {
    this.geo.getCoords().subscribe((coords) => {
      if (!coords) return;
      this.dataService
        .getNearestCarLocation(coords.lat, coords.lon)
        .subscribe((loc) => {
          if (!loc) return;
          const updates: any = {};
          if (!this.carSearchForm.get('pickup_id')?.value) {
            updates.pickup_id = loc.id;
          }
          const isSameDrop =
            this.carSearchForm.get('carRentalType')?.value ===
            CarRentalType.SAME_DROP;
          const dropEmpty = !this.carSearchForm.get('dropoff_id')?.value;
          if (isSameDrop || dropEmpty) {
            updates.dropoff_id = loc.id;
          }
          if (Object.keys(updates).length) {
            this.carSearchForm.patchValue(updates);
          }
          // Also persist to service and localStorage for consistency
          const updated = { ...this.carSearchForm.value };
          this.searchBarService.changeCarSearchRequest(updated);
        });
    });
  }

  // Explicit user action to use nearest pickup (and dropoff if Same drop-off or empty)
  useNearestPickup(): void {
    this.geo.getCoords().subscribe((coords) => {
      if (!coords) return;
      this.dataService
        .getNearestCarLocation(coords.lat, coords.lon)
        .subscribe((loc) => {
          if (!loc) return;
          const updates: any = { pickup_id: loc.id };
          const isSameDrop =
            this.carSearchForm.get('carRentalType')?.value ===
            CarRentalType.SAME_DROP;
          const dropEmpty = !this.carSearchForm.get('dropoff_id')?.value;
          if (isSameDrop || dropEmpty) {
            updates.dropoff_id = loc.id;
          }
          this.carSearchForm.patchValue(updates);
          this.searchBarService.changeCarSearchRequest({
            ...this.carSearchForm.value,
          });
        });
    });
  }

  onSearch() {
    this.getLocalStorageValues();
    if (this.carSearchForm.invalid) {
      this.carSearchForm.markAllAsTouched();
      const errors = this.carSearchForm.errors;
      console.log('Form is invalid', errors);
      return;
    }
    const d = { ...this.carSearchForm.value };
    // Format pickup_date and dropoff_date as 'YYYY-MM-DD' if they are Date objects or ISO strings
    const formatDate = (val: any) => {
      if (!val) return '';
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val))
        return val;
      const date = new Date(val);
      if (isNaN(date.getTime())) return val;
      return `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}`;
    };
    // Convert any time string to 24-hour format for API
    const to24Hour = (timeVal: any) => {
      if (!timeVal) return '';
      if (typeof timeVal === 'string') {
        // Accepts 'hh:mm' or 'hh:mm AM/PM' and always returns 'HH:mm'
        const timeRegex = /^(\d{1,2}):(\d{2})\s*([AP]M)?$/i;
        const match = timeRegex.exec(timeVal);
        if (match) {
          let hour = parseInt(match[1], 10);
          const minute = match[2];
          const ampm = match[3] ? match[3].toUpperCase() : '';
          if (ampm === 'PM' && hour < 12) hour += 12;
          if (ampm === 'AM' && hour === 12) hour = 0;
          return `${('0' + hour).slice(-2)}:${minute}`;
        }
        // If already 24h format
        const time24Regex = /^(\d{2}):(\d{2})$/;
        const match24 = time24Regex.exec(timeVal);
        if (match24) {
          return `${match24[1]}:${match24[2]}`;
        }
        return '';
      } else if (timeVal instanceof Date) {
        // If it's a Date object, extract hours and minutes
        const hour = timeVal.getHours();
        const minute = timeVal.getMinutes();
        return `${('0' + hour).slice(-2)}:${('0' + minute).slice(-2)}`;
      }
      return '';
    };
    d.pickup_date = formatDate(d.pickup_date);
    d.dropoff_date = formatDate(d.dropoff_date);
    d.pickup_time = to24Hour(d.pickup_time);
    d.dropoff_time = to24Hour(d.dropoff_time);
    this.searchBarService.changeCarSearchRequest(d as CarSearchRequest);
    // Build query string explicitly, skip undefined/null, ensure all values are string
    const params: string[] = [];
    const addParam = (key: string, value: any) => {
      if (value !== undefined && value !== null && value !== '') {
        params.push(`${key}=${encodeURIComponent(String(value))}`);
      }
    };
    addParam('carRentalType', d.carRentalType);
    addParam('pickup_id', d.pickup_id);
    addParam('dropoff_id', d.dropoff_id);
    addParam('pickup_date', d.pickup_date);
    addParam('dropoff_date', d.dropoff_date);
    addParam('pickup_time', d.pickup_time);
    addParam('dropoff_time', d.dropoff_time);
    addParam('driver_age', d.driver_age);
    addParam('country_res', d.country_res);
    addParam('currency', d.currency);
    addParam('language', d.language);
    const queryString = params.join('&');
    this.search.emit(queryString);
  }

  ngOnDestroy(): void {
    // Clean up body class on component destroy
    document.body.classList.remove('datepicker-open');
    this._autoSaveSub?.unsubscribe();
  }
}
